/***************************************************************************

    pageSeo.ts
    -------------------------
    last Update          : October 23, 2026
    by                   : SEO sprint 2
    copyright            : (C) 2025 by CIL4Sys Engineering

***************************************************************************/

export type PageSeoLang = "en" | "fr"

export interface PageSeoMeta {
	title: string
	description: string
}

/** Slug keys match Strapi page.name or static routes (contact-us). */
const CIL4SYS_PAGE_SEO: Record<string, Record<PageSeoLang, PageSeoMeta>> = {
	home: {
		en: {
			title: "CIL4Sys | Agile Systems Engineering & MBSE",
			description:
				"CIL4Sys helps R&D teams structure requirements, accelerate design, and validate with executable models. MBSE, Sim4Sys, VR and AI for complex systems. Contact us.",
		},
		fr: {
			title: "CIL4Sys | Ingénierie Système Agile & MBSE",
			description:
				"CIL4Sys aide les équipes R&D à structurer les exigences, accélérer la conception et valider avec des modèles exécutables. MBSE, Sim4Sys, VR et IA. Contactez-nous.",
		},
	},
	services: {
		en: {
			title: "Systems Engineering Services | CIL4Sys",
			description:
				"Model-based specs, digital transformation, custom engineering tools, and model-generated code for HIL and digital twins. Customer In the Loop with Sim4Sys.",
		},
		fr: {
			title: "Services d'ingénierie système | CIL4Sys",
			description:
				"Spécifications basées modèles, transformation digitale, outils d'ingénierie sur mesure, et code issu des modèles pour HIL et jumeaux numériques. Avec Sim4Sys.",
		},
	},
	solutions: {
		en: {
			title: "Sim4Sys Solutions: Designer, Virtual Bench, SyneSys",
			description:
				"CIL4Sys solutions: Sim4Sys Designer, Virtual Bench SaaS, and SyneSys AI companion. Model, simulate, and validate systems earlier in the lifecycle.",
		},
		fr: {
			title: "Solutions Sim4Sys : Designer, Virtual Bench, SyneSys",
			description:
				"Solutions CIL4Sys : Sim4Sys Designer, Virtual Bench SaaS et compagnon IA SyneSys. Modélisez, simulez et validez vos systèmes plus tôt dans le cycle.",
		},
	},
	formation: {
		en: {
			title: "MBSE & SysML Training Courses | CIL4Sys",
			description:
				"Hands-on MBSE and SysML training (v1/v2) with Sim4Sys. Build modeling skills for complex systems. See upcoming sessions or request a private course.",
		},
		fr: {
			title: "Formations MBSE & SysML | CIL4Sys",
			description:
				"Formations MBSE et SysML (v1/v2) pratiques avec Sim4Sys. Montez en compétence sur les systèmes complexes. Sessions à venir ou formation sur mesure.",
		},
	},
	usecases: {
		en: {
			title: "MBSE Use Cases: Defense & Industry | CIL4Sys",
			description:
				"Real MBSE use cases: defense systems of systems, mine warfare, drone vessel inspection. See how CIL4Sys and Sim4Sys accelerate specification and V&V.",
		},
		fr: {
			title: "Cas d'usage MBSE : défense & industrie | CIL4Sys",
			description:
				"Cas d'usage MBSE réels : systèmes de systèmes défense, guerre des mines, inspection navale par drone. Comment CIL4Sys et Sim4Sys accélèrent spécification et V&V.",
		},
	},
	about_us: {
		en: {
			title: "About CIL4Sys Engineering | MBSE Since 2015",
			description:
				"CIL4Sys Engineering was founded in 2015 around Customer In the Loop: UML/SysML models validated with stakeholders via a functional digital mock-up. Meet the team story.",
		},
		fr: {
			title: "À propos de CIL4Sys Engineering | MBSE depuis 2015",
			description:
				"CIL4Sys Engineering est née en 2015 autour de Customer In the Loop : modèles UML/SysML validés avec les parties prenantes via une maquette numérique fonctionnelle.",
		},
	},
	"contact-us": {
		en: {
			title: "Contact CIL4Sys Engineering | Get in Touch",
			description:
				"Have a systems engineering project or a question about MBSE or Sim4Sys? Contact the CIL4Sys team in Paris. We reply as soon as possible.",
		},
		fr: {
			title: "Contactez CIL4Sys Engineering",
			description:
				"Un projet d'ingénierie système ou une question sur le MBSE ou Sim4Sys ? Contactez l'équipe CIL4Sys à Paris. Nous vous répondons rapidement.",
		},
	},
	legal: {
		en: {
			title: "Legal Notice | CIL4Sys Engineering",
			description:
				"Legal notice and company information for CIL4Sys Engineering (Paris). Publisher, hosting, and intellectual property details.",
		},
		fr: {
			title: "Mentions légales | CIL4Sys Engineering",
			description:
				"Mentions légales et informations société de CIL4Sys Engineering (Paris). Éditeur, hébergement et propriété intellectuelle.",
		},
	},
	resources: {
		en: {
			title: "MBSE & Sim4Sys Resources | CIL4Sys",
			description:
				"Guides and definitions on Customer In the Loop, MBSE, SysML, and the Sim4Sys toolchain from CIL4Sys Engineering.",
		},
		fr: {
			title: "Ressources MBSE & Sim4Sys | CIL4Sys",
			description:
				"Guides et définitions sur Customer In the Loop, le MBSE, SysML et la chaîne Sim4Sys par CIL4Sys Engineering.",
		},
	},
	"resources/customer-in-the-loop": {
		en: {
			title: "What Is Customer In the Loop? MBSE Method | CIL4Sys",
			description:
				"Customer In the Loop is CIL4Sys’s MBSE method: stakeholders continuously validate system behavior in a shared functional digital mock-up. Definition and benefits.",
		},
		fr: {
			title: "Customer In the Loop : méthode MBSE | CIL4Sys",
			description:
				"Customer In the Loop est la méthode MBSE de CIL4Sys : validation continue du comportement système dans une maquette numérique fonctionnelle partagée.",
		},
	},
	"resources/sysml-v2-vs-v1": {
		en: {
			title: "SysML v2 vs SysML v1 for MBSE Teams | CIL4Sys",
			description:
				"Compare SysML v2 and SysML v1: textual syntax, API, migration timing, and CIL4Sys SysML v2 training. Practical guide for MBSE teams.",
		},
		fr: {
			title: "SysML v2 vs SysML v1 pour le MBSE | CIL4Sys",
			description:
				"Comparer SysML v2 et SysML v1 : syntaxe textuelle, API, timing de migration, et formation SysML v2 CIL4Sys. Guide pratique pour équipes MBSE.",
		},
	},
	"resources/sim4sys-designer": {
		en: {
			title: "Sim4Sys Designer: Guided MBSE Add-ons | CIL4Sys",
			description:
				"Sim4Sys Designer add-ons for Papyrus, Rhapsody, PTC Modeler, and CATIA Magic: guided modeling, consistency, and generation of specs and executables.",
		},
		fr: {
			title: "Sim4Sys Designer : add-ons MBSE guidés | CIL4Sys",
			description:
				"Add-ons Sim4Sys Designer pour Papyrus, Rhapsody, PTC Modeler et CATIA Magic : modélisation guidée, cohérence, génération de spécifications et exécutables.",
		},
	},
	"resources/functional-digital-mockup": {
		en: {
			title: "Functional Digital Mock-up Explained | CIL4Sys",
			description:
				"Functional digital mock-up vs CAD: validate system behavior early with executable models and Sim4Sys Virtual Bench scenario-based V&V.",
		},
		fr: {
			title: "Maquette numérique fonctionnelle expliquée | CIL4Sys",
			description:
				"Maquette numérique fonctionnelle vs CAO : valider tôt le comportement système avec modèles exécutables et Sim4Sys Virtual Bench.",
		},
	},
	"resources/synesys-hitl": {
		en: {
			title: "SyneSys AI Companion for Systems Engineering | CIL4Sys",
			description:
				"SyneSys: ontology-guided, human-in-the-loop AI for turning engineering documents into structured models with SysML v2 export. SaaS or on-prem.",
		},
		fr: {
			title: "SyneSys compagnon IA ingénierie système | CIL4Sys",
			description:
				"SyneSys : IA human-in-the-loop guidée par ontologies pour transformer des documents en modèles structurés, export SysML v2. SaaS ou on-prem.",
		},
	},
}

/**
 * Resolve page SEO key from Gatsby pageContext.page and/or URL path.
 */
export function resolvePageSeoKey(aPageFromContext: string | undefined, aPath: string | undefined): string {
	if (aPageFromContext && aPageFromContext.trim() !== "") {
		return aPageFromContext === "home" ? "home" : aPageFromContext
	}
	const raw = (aPath || "/").split("?")[0]
	const withoutLang = raw.replace(/^\/(en|fr)(?=\/|$)/, "") || "/"
	const cleaned = withoutLang.replace(/\/+$/, "") || "/"
	if (cleaned === "/" || cleaned === "") {
		return "home"
	}
	return cleaned.replace(/^\//, "")
}

export function getPageSeo(aPageKey: string, aLang: PageSeoLang): PageSeoMeta | null {
	if (process.env.GATSBY_STRAPI_PROJECT === "squaremiles") {
		return null
	}
	const entry = CIL4SYS_PAGE_SEO[aPageKey]
	if (!entry) {
		return null
	}
	return entry[aLang] || entry.en
}
